/**
 * Eigene Google-reCAPTCHA-Integration (v2 "Ich bin kein Roboter" und v3 unsichtbar).
 *
 * Analog zu TurnstileFormGuard.js wird reCAPTCHA nicht per Cookie-Consent
 * blockiert. Stattdessen fängt dieses Modul den Submit der in den Settings
 * ausgewählten Formulare ab und zeigt zunächst ein Modal, das auf die
 * notwendige Verbindung zu Google hinweist. Erst nach Bestätigung wird
 * www.google.com/recaptcha/api.js nachgeladen und die Prüfung durchgeführt -
 * bei v2 über eine Checkbox im Modal, bei v3 unsichtbar im Hintergrund -
 * bevor das Formular danach abgeschickt wird.
 */
export class RecaptchaFormGuard {
    constructor(banner) {
        this.banner = banner;
        this.config = (banner?.config || window.pixelmateConfig || {}).recaptcha || {};

        this.scriptPromise = null;
        this.widgetId = null;
        this.widgetContainer = null;
        this.overlayEl = null;
        this.modalEl = null;
        this.statusEl = null;

        this.pendingForm = null;
        this.pendingSubmitter = null;

        // Fokus-Rückgabe und Escape-Handling für das Modal (Barrierefreiheit).
        this.lastFocused = null;
        this.keydownHandler = (e) => this.handleKeydown(e);

        if (!this.config.enabled || !this.config.siteKey || !Array.isArray(this.config.selectors) || this.config.selectors.length === 0) {
            this.debug('inactive - config missing/incomplete', this.config);
            return;
        }

        this.isV2 = this.config.version === 'v2';
        this.selector = this.config.selectors.join(', ');
        this.debug('active, watching selector', this.selector);
        this.init();
    }

    // Loggt nur, wenn window.pixelmateConfig.debug true ist (WP_DEBUG) oder
    // ?pixelmate_recaptcha_debug=1 in der URL steht - damit lässt sich das
    // Verhalten live in der Konsole nachvollziehen, ohne die Konsole für
    // alle Besucher vollzuschreiben.
    debug(...args) {
        const forced = typeof window !== 'undefined' && window.location && window.location.search.indexOf('pixelmate_recaptcha_debug=1') !== -1;
        if (forced || (window.pixelmateConfig && window.pixelmateConfig.debug)) {
            console.log('[PixelmateRecaptcha]', ...args);
        }
    }

    init() {
        // Capture-Phase: fängt den Submit ab, bevor Formular-Plugins (CF7,
        // WPForms, Gravity Forms, ...) ihn per AJAX weiterverarbeiten.
        document.addEventListener('submit', (e) => this.handleSubmit(e), true);
    }

    handleSubmit(e) {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) {
            this.debug('submit ignored - target is not a form', e.target);
            return;
        }
        if (!this.matchesSelector(form)) {
            this.debug('submit ignored - form does not match selector', this.selector, form);
            return;
        }

        // Formular wurde soeben mit gültigem Token erneut abgeschickt - durchlassen.
        if (form.dataset.pixelmateRecaptchaVerified === '1') {
            this.debug('submit passed through - already verified', form);
            delete form.dataset.pixelmateRecaptchaVerified;
            return;
        }

        this.debug('intercepting submit, opening modal', form);
        e.preventDefault();
        e.stopImmediatePropagation();

        this.pendingForm = form;
        this.pendingSubmitter = e.submitter || null;
        this.openModal();
    }

    matchesSelector(form) {
        try {
            return form.matches(this.selector);
        } catch (err) {
            return false;
        }
    }

    // ==================== MODAL ====================

    openModal() {
        if (!this.modalEl) {
            this.buildModal();
        }

        // Aktuellen Fokus merken, um ihn beim Schließen zurückzugeben.
        this.lastFocused = document.activeElement;

        this.setStatus('intro');
        document.body.appendChild(this.overlayEl);
        document.body.appendChild(this.modalEl);

        document.addEventListener('keydown', this.keydownHandler, true);

        requestAnimationFrame(() => {
            this.overlayEl.style.opacity = '1';
            this.modalEl.style.opacity = '1';
            this.modalEl.style.transform = 'translate(-50%, -50%) scale(1)';

            // Fokus in den Dialog verlegen (Barrierefreiheit / Tastaturbedienung).
            const confirmBtn = this.modalEl.querySelector('[data-action="confirm"]');
            if (confirmBtn) {
                confirmBtn.focus();
            }
        });
    }

    closeModal() {
        if (!this.modalEl) return;

        document.removeEventListener('keydown', this.keydownHandler, true);

        this.overlayEl.style.opacity = '0';
        this.modalEl.style.opacity = '0';
        this.modalEl.style.transform = 'translate(-50%, -50%) scale(0.95)';

        window.setTimeout(() => {
            this.overlayEl?.remove();
            this.modalEl?.remove();
        }, 200);

        // Fokus auf das vorherige Element zurückgeben.
        if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
            this.lastFocused.focus();
        }
        this.lastFocused = null;
    }

    handleKeydown(e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
            e.preventDefault();
            this.cancel();
        }
    }

    // Das öffentliche Stylesheet pixelmate.css wird im Frontend nicht geladen
    // (siehe PixelmateBanner.js) - das Modal stylt sich deshalb wie der Rest
    // des Plugins vollständig über Inline-Styles, statt sich auf die
    // gleichnamigen CSS-Klassen im (ungeladenen) Stylesheet zu verlassen.
    buildModal() {
        const overlay = document.createElement('div');
        overlay.className = 'pixelmate-recaptcha-modal-overlay';
        overlay.style.cssText = `
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0, 0, 0, 0.6); z-index: 2147483001;
            opacity: 0; transition: opacity 0.2s ease;
        `;

        const modal = document.createElement('div');
        modal.className = 'pixelmate-recaptcha-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-labelledby', 'pixelmate-recaptcha-modal-title');
        modal.style.cssText = `
            position: fixed; top: 50%; left: 50%;
            transform: translate(-50%, -50%) scale(0.95);
            width: 90%; max-width: 420px; background: #ffffff; color: #333333;
            border-radius: 8px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
            padding: 24px; z-index: 2147483002; opacity: 0;
            transition: opacity 0.2s ease, transform 0.2s ease; box-sizing: border-box;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
        `;

        modal.innerHTML = `
            <h3 id="pixelmate-recaptcha-modal-title" class="pixelmate-recaptcha-modal-title" style="margin: 0 0 12px 0; font-size: 18px; font-weight: 600; line-height: 1.3;">${this.text('title')}</h3>
            <p class="pixelmate-recaptcha-modal-text" style="margin: 0 0 16px 0; font-size: 14px; line-height: 1.5;">${this.text('intro')}</p>
            <div class="pixelmate-recaptcha-modal-status" style="font-size: 13px; color: #666666; min-height: 18px; margin-bottom: 8px;"></div>
            <div class="pixelmate-recaptcha-modal-widget" style="display: flex; justify-content: center; margin-bottom: 8px;"></div>
            <div class="pixelmate-recaptcha-modal-actions" style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 8px;">
                <button type="button" class="pixelmate-recaptcha-modal-btn pixelmate-recaptcha-modal-btn-secondary" data-action="cancel" style="border: none; padding: 10px 18px; font-size: 14px; font-weight: 600; border-radius: 4px; cursor: pointer; white-space: nowrap; background: transparent; color: #666666;">${this.text('cancel')}</button>
                <button type="button" class="pixelmate-recaptcha-modal-btn pixelmate-recaptcha-modal-btn-primary" data-action="confirm" style="border: none; padding: 10px 18px; font-size: 14px; font-weight: 600; border-radius: 4px; cursor: pointer; white-space: nowrap; background: #0073aa; color: #ffffff;">${this.text('confirm')}</button>
            </div>
        `;

        modal.querySelector('[data-action="cancel"]').addEventListener('click', () => this.cancel());
        modal.querySelector('[data-action="confirm"]').addEventListener('click', () => this.onConfirm());
        overlay.addEventListener('click', () => this.cancel());

        this.overlayEl = overlay;
        this.modalEl = modal;
        this.statusEl = modal.querySelector('.pixelmate-recaptcha-modal-status');
        this.widgetContainer = modal.querySelector('.pixelmate-recaptcha-modal-widget');
    }

    text(key) {
        // Deutsche Standardtexte als Fallback. Die tatsächlich angezeigten Texte
        // liefert die Serverseite übersetzt via config.texts (SettingsLocalizer),
        // damit das Modal der WordPress-Sprache folgt statt immer Deutsch zu sein.
        const defaults = {
            title: 'Sicherheitsprüfung erforderlich',
            intro: this.isV2
                ? 'Zum Schutz dieses Formulars vor Spam und automatisierten Zugriffen muss eine Verbindung zu Google (reCAPTCHA) aufgebaut werden. Dabei wird deine IP-Adresse zur Sicherheitsprüfung an Google übermittelt.'
                : 'Zum Schutz dieses Formulars vor Spam und automatisierten Zugriffen muss eine Verbindung zu Google (reCAPTCHA) aufgebaut werden. Die Prüfung läuft unsichtbar im Hintergrund; dabei wird deine IP-Adresse zur Sicherheitsprüfung an Google übermittelt.',
            confirm: 'Sicherheitsprüfung starten',
            cancel: 'Abbrechen',
            loading: 'Verbindung zu Google wird aufgebaut …',
            error: 'Die Sicherheitsprüfung konnte nicht geladen werden. Bitte versuche es erneut.',
            retry: 'Erneut versuchen',
        };
        const texts = this.config.texts || {};
        return texts[key] || defaults[key] || '';
    }

    setStatus(state) {
        if (!this.statusEl) return;

        const confirmBtn = this.modalEl.querySelector('[data-action="confirm"]');
        const cancelBtn = this.modalEl.querySelector('[data-action="cancel"]');

        if (state === 'intro') {
            this.statusEl.textContent = '';
            this.widgetContainer.innerHTML = '';
            confirmBtn.style.display = '';
            confirmBtn.disabled = false;
            confirmBtn.textContent = this.text('confirm');
            cancelBtn.style.display = '';
        } else if (state === 'loading') {
            this.statusEl.textContent = this.text('loading');
            confirmBtn.style.display = 'none';
        } else if (state === 'widget') {
            this.statusEl.textContent = '';
            confirmBtn.style.display = 'none';
        } else if (state === 'error') {
            this.statusEl.textContent = this.text('error');
            confirmBtn.style.display = '';
            confirmBtn.disabled = false;
            confirmBtn.textContent = this.text('retry');
            cancelBtn.style.display = '';
        }
    }

    cancel() {
        this.pendingForm = null;
        this.pendingSubmitter = null;
        this.closeModal();
    }

    onConfirm() {
        this.debug('modal confirmed, loading www.google.com/recaptcha/api.js');
        this.setStatus('loading');

        this.loadScript()
            .then(() => this.runChallenge())
            .catch((err) => {
                this.debug('failed to load reCAPTCHA script', err);
                this.setStatus('error');
            });
    }

    // ==================== RECAPTCHA ====================

    loadScript() {
        if (window.grecaptcha) {
            return Promise.resolve();
        }

        if (this.scriptPromise) {
            return this.scriptPromise;
        }

        this.scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            // v3 rendert kein Widget und lädt sich selbst über den render-Parameter,
            // v2 rendert die Checkbox später explizit per grecaptcha.render().
            script.src = this.isV2
                ? 'https://www.google.com/recaptcha/api.js?render=explicit'
                : `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(this.config.siteKey)}`;
            script.async = true;
            script.defer = true;
            script.addEventListener('load', () => resolve());
            script.addEventListener('error', () => {
                this.scriptPromise = null;
                reject(new Error('reCAPTCHA script failed to load'));
            });
            document.head.appendChild(script);
        });

        return this.scriptPromise;
    }

    runChallenge() {
        if (!window.grecaptcha) {
            this.setStatus('error');
            return;
        }

        window.grecaptcha.ready(() => {
            if (this.isV2) {
                this.renderV2Widget();
            } else {
                this.executeV3();
            }
        });
    }

    renderV2Widget() {
        if (!this.widgetContainer) {
            this.setStatus('error');
            return;
        }

        this.setStatus('widget');
        this.widgetContainer.innerHTML = '';

        this.widgetId = window.grecaptcha.render(this.widgetContainer, {
            sitekey: this.config.siteKey,
            theme: this.config.theme || 'light',
            size: this.config.size || 'normal',
            callback: (token) => this.onToken(token),
            'error-callback': () => this.setStatus('error'),
            'expired-callback': () => {
                if (this.widgetId !== null) {
                    window.grecaptcha.reset(this.widgetId);
                }
            },
        });
    }

    executeV3() {
        // v3 zeigt keine Checkbox - die Prüfung läuft unsichtbar, die Sichtbarkeit
        // des Modals bis dahin dient allein der Transparenz gegenüber Nutzern.
        this.setStatus('loading');

        window.grecaptcha
            .execute(this.config.siteKey, { action: this.config.action || 'submit' })
            .then((token) => this.onToken(token))
            .catch((err) => {
                this.debug('grecaptcha.execute failed', err);
                this.setStatus('error');
            });
    }

    onToken(token) {
        const form = this.pendingForm;
        const submitter = this.pendingSubmitter;

        if (!form) return;

        this.debug('token received, re-submitting form', form);
        this.setTokenOnForm(form, token);
        this.pendingForm = null;
        this.pendingSubmitter = null;
        this.closeModal();

        form.dataset.pixelmateRecaptchaVerified = '1';

        if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter || undefined);
        } else {
            form.submit();
        }
    }

    setTokenOnForm(form, token) {
        let input = form.querySelector('input[name="g-recaptcha-response"]');
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'g-recaptcha-response';
            form.appendChild(input);
        }
        input.value = token;
    }
}

